import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../config';

function Contact() {
  const { isAuthenticated, token } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const config = {
        headers: isAuthenticated ? { Authorization: `Bearer ${token}` } : {}
      };

      const response = await axios.post(
        `${API_URL}/contact/submit`,
        formData,
        config
      );

      setAlert({
        open: true,
        message: response.data.message,
        severity: 'success'
      });

      // Clear form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Failed to send message. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 6,
          backgroundColor: '#0a0b0e',
          borderRadius: 4,
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Typography 
          variant="h2" 
          component="h1" 
          gutterBottom
          align="center"
          sx={{ 
            fontWeight: 600,
            color: '#00ffea',
            mb: 3
          }}
        >
          Contact Us
        </Typography>
        
        <Typography 
          variant="h5" 
          align="center"
          sx={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            mb: 6
          }}
        >
          Have a question or feedback? We'd love to hear from you.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              required
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#141519',
                }
              }}
            />
            <TextField
              required
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#141519',
                }
              }}
            />
          </Box>

          <TextField
            required
            fullWidth
            label="Subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#141519',
              }
            }}
          />

          <TextField
            required
            fullWidth
            label="Message"
            name="message"
            multiline
            rows={6}
            value={formData.message}
            onChange={handleChange}
            sx={{
              mb: 4,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#141519',
              }
            }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            endIcon={loading ? <CircularProgress size={20} sx={{ color: '#000' }} /> : <SendIcon />}
            sx={{ 
              minWidth: 200,
              height: 48,
              borderRadius: 50,
              background: 'linear-gradient(90deg, #00ffea 0%, #00b8ff 100%)',
              color: '#000',
              fontSize: '1.1rem',
              fontWeight: 500,
              textTransform: 'none',
              display: 'block',
              mx: 'auto',
              '&:hover': {
                background: 'linear-gradient(90deg, #00b8ff 0%, #00ffea 100%)',
              }
            }}
          >
            {loading ? 'Sending...' : 'Send Message'}
          </Button>
        </Box>

        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseAlert} 
            severity={alert.severity}
            sx={{ width: '100%' }}
          >
            {alert.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
}

export default Contact; 